/**
 * 分镜帧 / 候选帧专用全屏预览。
 *
 * 与通用 Lightbox 的区别：
 * - 支持前后翻图（← / → 键 + 屏幕两侧的翻页按钮 + 顶部「N / 总数」徽标）
 * - 底部支持父组件注入功能按钮（按当前 item 动态计算）
 *
 * 契约：
 * - items 数组的顺序由父组件决定（如：先序列、后候选）
 * - currentIndex 越界时（删除导致）自动 clamp；items 为空时调用 onClose
 * - actions 由父组件按当前 item 计算，避免依赖闭包导致的"操作落到旧帧"
 */

import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { resolveBrowserMediaUrl } from '../../../../utils/storageUrl';

export interface FrameLightboxItem {
  id: string;
  url: string;
  /** 顶部徽标显示（如"序列 1"、"候选 3"） */
  label?: string;
}

export interface FrameLightboxAction {
  key: string;
  icon: ReactNode;
  label: string;
  onClick: (item: FrameLightboxItem, index: number) => void | Promise<void>;
  disabled?: boolean;
  variant?: 'default' | 'primary' | 'danger';
  /** false 时不渲染（用于"上移在第一张时不显示"等场景） */
  show?: boolean;
  title?: string;
}

interface Props {
  items: FrameLightboxItem[];
  currentIndex: number;
  onIndexChange: (i: number) => void;
  onClose: () => void;
  /**
   * 按当前 item 动态构造 actions。
   * 每次翻图都会重新调用，所以可以放心使用最新的外部状态做计算。
   */
  buildActions: (item: FrameLightboxItem, index: number) => FrameLightboxAction[];
}

export function FrameLightbox({
  items,
  currentIndex,
  onIndexChange,
  onClose,
  buildActions,
}: Props) {
  // items 为空 → 自动关闭
  useEffect(() => {
    if (items.length === 0) onClose();
  }, [items.length, onClose]);

  // currentIndex 越界 → clamp 到合法范围
  useEffect(() => {
    if (items.length === 0) return;
    if (currentIndex < 0) onIndexChange(0);
    else if (currentIndex >= items.length) onIndexChange(items.length - 1);
  }, [items.length, currentIndex, onIndexChange]);

  const safeIndex = Math.max(0, Math.min(items.length - 1, currentIndex));
  const current = items[safeIndex];

  const goPrev = () => {
    if (items.length <= 1) return;
    onIndexChange(safeIndex > 0 ? safeIndex - 1 : items.length - 1);
  };
  const goNext = () => {
    if (items.length <= 1) return;
    onIndexChange(safeIndex < items.length - 1 ? safeIndex + 1 : 0);
  };

  // 键盘交互：Esc 关闭，← / → 翻页
  useEffect(() => {
    if (!current) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
        return;
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, items.length, safeIndex]);

  if (!current) return null;
  if (typeof document === 'undefined') return null;

  const actions = buildActions(current, safeIndex).filter((a) => a.show !== false);

  const variantClass = (v: FrameLightboxAction['variant']) => {
    switch (v) {
      case 'primary':
        return 'bg-amber-500 text-white hover:bg-amber-600 disabled:bg-slate-500';
      case 'danger':
        return 'bg-rose-500/90 text-white hover:bg-rose-600 disabled:bg-slate-500';
      default:
        return 'bg-white/95 text-slate-800 hover:bg-white disabled:bg-white/50 disabled:text-slate-400';
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex flex-col bg-black/92 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* 顶部条：徽标 + 关闭 */}
      <div
        className="flex items-center justify-between px-4 py-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 rounded-full bg-black/80 px-3.5 py-1.5 text-xs font-semibold text-white shadow-xl ring-2 ring-white/40">
          <span>
            {safeIndex + 1} / {items.length}
          </span>
          {current.label && (
            <>
              <span className="text-white/60">·</span>
              <span>{current.label}</span>
            </>
          )}
        </div>
        <button
          type="button"
          aria-label="关闭"
          onClick={onClose}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-black/80 text-white shadow-xl ring-2 ring-white/40 transition hover:bg-black hover:ring-white/60"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* 中间图 + 左右翻页按钮 */}
      <div
        className="relative flex flex-1 items-center justify-center px-4"
        onClick={(e) => e.stopPropagation()}
      >
        {items.length > 1 && (
          <button
            type="button"
            onClick={goPrev}
            aria-label="上一张"
            className="absolute left-3 z-[10] flex h-12 w-12 items-center justify-center rounded-full bg-black/80 text-white shadow-xl ring-2 ring-white/40 transition hover:bg-black hover:ring-white/60 sm:left-6 sm:h-14 sm:w-14"
          >
            <ChevronLeft className="h-6 w-6 sm:h-7 sm:w-7" />
          </button>
        )}

        <img
          src={resolveBrowserMediaUrl(current.url)}
          alt=""
          className="max-h-[calc(100vh-220px)] max-w-[min(92vw,1400px)] rounded-xl object-contain shadow-2xl"
          draggable={false}
        />

        {items.length > 1 && (
          <button
            type="button"
            onClick={goNext}
            aria-label="下一张"
            className="absolute right-3 z-[10] flex h-12 w-12 items-center justify-center rounded-full bg-black/80 text-white shadow-xl ring-2 ring-white/40 transition hover:bg-black hover:ring-white/60 sm:right-6 sm:h-14 sm:w-14"
          >
            <ChevronRight className="h-6 w-6 sm:h-7 sm:w-7" />
          </button>
        )}
      </div>

      {/* 底部操作栏 */}
      <div
        className="flex flex-wrap items-center justify-center gap-2 px-4 py-4"
        onClick={(e) => e.stopPropagation()}
      >
        {actions.length === 0 ? (
          <div className="text-[11px] text-white/50">无可用操作</div>
        ) : (
          actions.map((a) => (
            <button
              key={a.key}
              type="button"
              onClick={async () => {
                if (a.disabled) return;
                await a.onClick(current, safeIndex);
              }}
              disabled={a.disabled}
              title={a.title ?? a.label}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold shadow-lg transition disabled:cursor-not-allowed ${variantClass(
                a.variant
              )}`}
            >
              {a.icon}
              <span>{a.label}</span>
            </button>
          ))
        )}
      </div>
    </div>,
    document.body
  );
}
